import React, { useEffect, useState } from 'react';
import { List, Card, Avatar, Row, Col, Button, Skeleton } from 'antd';
import { Link } from 'react-router-dom';
import { DocumentNode, useQuery } from '@apollo/client';
import AdminPlanFilters from '../Plans/AdminPlanFilters';
import { useAuth } from '../../providers/AuthProvider';
import { IBaseFilter } from '../../types/plans';
interface FilterValue {
  value: string | number | boolean | string[] | null;
  operator?: string;
}

interface Filters {
  [key: string]: FilterValue | string | number | boolean | string[] | null;
}

interface FiltersProps {
  prefix: string;
  filters: Filters;
  setFilters: (filters: Filters) => void;
  roles?: string[];
}

const Filters: React.FC<FiltersProps> = ({ prefix, filters, setFilters, roles }) => {
  if (prefix === 'plan') {
    return (
      <AdminPlanFilters
        filters={filters as IBaseFilter}
        setFilters={setFilters as (filters: IBaseFilter) => void}
        roles={roles}
      />
    );
  }
  return null;
};

export interface ListItem {
  id: string;
  title?: string;
  name?: string;
  logo?: string;
  image?: string;
  description?: string;
  email?: string;
  map?: {
    address?: string;
  };
  [key: string]: string | number | boolean | null | undefined | string[] | object;
}

export interface ListContainerProps {
  prefix: string;
  query: DocumentNode;
  editable?: boolean;
  canCreate?: boolean;
  inline?: boolean;
  adminView?: boolean;
  CustomCard?: React.ComponentType<{
    item: ListItem;
    refetch: () => void;
    prefix: string;
    roles?: string[];
  }>;
  defaultPageSize?: number;
}

const ListContainer: React.FC<ListContainerProps> = ({
  prefix,
  query,
  editable = true,
  canCreate = true,
  inline = false,
  adminView = false,
  CustomCard,
  defaultPageSize = 20,
}) => {
  const { user } = useAuth();
  const [filters, setFilters] = useState<Filters>({});
  const [pageSize, setPageSize] = useState(defaultPageSize);
  const [currentPage, setCurrentPage] = useState(1);
  const { data, refetch, loading } = useQuery(query, {
    variables: adminView ? { ...filters } : { ...filters, userId: user?.id },
    fetchPolicy: 'network-only',
  });

  const items: ListItem[] = (data && data[Object.keys(data)[0]]) || [];

  useEffect(() => {
    refetch();
  }, [data, filters, refetch]);
  const getActions = (item: ListItem) => {
    const actions = [
      <Link key={`link-${item.id}`} to={`/${adminView ? 'admin/' : ''}${prefix}/${item.id}`}>
        View
      </Link>,
    ];
    if (editable) {
      actions.push(
        <Link key={`edit-${item.id}`} to={`/admin/${prefix}/edit/${item.id}`}>
          Edit
        </Link>
      );
    }
    return actions;
  };

  return (
    <>
      <Row>
        <Col span={24}>
          <Filters prefix={prefix} filters={filters} setFilters={setFilters} roles={user?.roles} />
        </Col>
        {canCreate && (
          <Col span={24}>
            <Link to={`${adminView ? '/admin' : ''}/${prefix}/new`}>
              <Button type="primary" shape="round">
                Create
              </Button>
            </Link>
          </Col>
        )}
      </Row>
      <Row>
        <Col span={24}>
          <Card style={{ width: '100%' }} variant="borderless">
            <List
              grid={!inline ? { gutter: 16, xs: 1, sm: 2, md: 3, lg: 4, xl: 4, xxl: 4 } : undefined}
              dataSource={items}
              loading={loading}
              pagination={{
                onChange: page => {
                  setCurrentPage(page);
                },
                current: currentPage,
                pageSize: pageSize,
                position: items.length > pageSize ? 'both' : 'bottom',
                showSizeChanger: true,
                responsive: true,
                showLessItems: true,
                showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
                onShowSizeChange: (current, size) => {
                  setPageSize(size);
                },
              }}
              renderItem={item =>
                CustomCard ? (
                  <List.Item>
                    <CustomCard refetch={refetch} item={item} prefix={prefix} roles={user?.roles} />
                  </List.Item>
                ) : inline ? (
                  <List.Item actions={getActions(item)}>
                    <Skeleton avatar title={false} loading={false} active>
                      <List.Item.Meta
                        avatar={
                          (item.logo || item.image) && <Avatar src={item.logo || item.image} />
                        }
                        title={
                          <Link to={`/${adminView ? 'admin/' : ''}${prefix}/${item.id}`}>
                            {item.title || item.name}
                          </Link>
                        }
                        description={item.description || item.email}
                      />
                    </Skeleton>
                  </List.Item>
                ) : (
                  <List.Item>
                    <Card
                      hoverable
                      cover={
                        (item.logo || item.image) && (
                          <img
                            alt=""
                            src={item.logo || item.image}
                            style={{ maxHeight: 200, objectFit: 'cover' }}
                          />
                        )
                      }
                      actions={getActions(item)}>
                      <Card.Meta
                        title={<Link to={`/${prefix}/${item.id}`}>{item.title || item.name}</Link>}
                        description={item.description || item.email}
                      />
                    </Card>
                  </List.Item>
                )
              }
            />
          </Card>
        </Col>
      </Row>
    </>
  );
};

export default ListContainer;
